import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  FaAward,
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronRight,
  FaCommentAlt,
  FaCopy,
  FaFlag,
  FaFire,
  FaSpinner,
  FaUserFriends,
  FaUserPlus,
  FaUserMinus,
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { socialService, userService } from '../../services/apiService';
import SocialNav from '../../components/SocialNav';
import ReportModal from '../../components/ReportModal';
import DevHubGallery from '../../components/DevHubGallery';
import SkillsShowcase from '../../components/SkillsShowcase';
import AvatarEditor, { AvatarEditTrigger } from '../../components/AvatarEditor';
import './UserProfile.css';
import './SocialPremium.css';

function formatDate(value, language, options = { dateStyle: 'long' }) {
  if (!value) return '—';
  try {
    const locale = language === 'en' ? 'en-US' : 'pt-BR';
    return new Intl.DateTimeFormat(locale, options).format(new Date(value));
  } catch {
    return '—';
  }
}

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name || '?').slice(0, 2).toUpperCase();
}

export default function UserProfile() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { username } = useParams();
  const navigate = useNavigate();
  const { user: me, isAuthenticated, updateUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [hubCategories, setHubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [followBusy, setFollowBusy] = useState(false);
  const [listOpen, setListOpen] = useState(null);
  const [listUsers, setListUsers] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);

  const displayName = profile?.profile?.displayName || profile?.username || '';

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [profileRes, hubRes] = await Promise.all([
        userService.getPublicProfile(username),
        socialService.getUserHub(username),
      ]);
      setProfile(profileRes.data.profile);
      setHubCategories(hubRes.data.categories || []);
    } catch (err) {
      setProfile(null);
      setHubCategories([]);
      setError(err.message || t('profile.notFound'));
    } finally {
      setLoading(false);
    }
  }, [t, username]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const openList = async (type) => {
    if (!profile) return;
    setListOpen(type);
    setListLoading(true);
    setListUsers([]);
    try {
      const res = type === 'followers'
        ? await socialService.getFollowers(profile.username, { limit: 40 })
        : await socialService.getFollowing(profile.username, { limit: 40 });
      setListUsers(res.data.users || []);
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    } finally {
      setListLoading(false);
    }
  };

  const handleFollowToggle = async () => {
    if (!isAuthenticated) {
      navigate('/entrar', { state: { from: `/perfil/${username}` } });
      return;
    }
    if (!profile || profile.isSelf) return;

    setFollowBusy(true);
    setStatus(null);
    try {
      if (profile.isFollowing) {
        await socialService.unfollow(profile.username);
        setProfile((current) => ({
          ...current,
          isFollowing: false,
          followersCount: Math.max(0, (current.followersCount || 0) - 1),
        }));
      } else {
        await socialService.follow(profile.username);
        setProfile((current) => ({
          ...current,
          isFollowing: true,
          followersCount: (current.followersCount || 0) + 1,
        }));
      }
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    } finally {
      setFollowBusy(false);
    }
  };

  const handleReportUser = () => {
    if (!isAuthenticated) {
      navigate('/entrar', { state: { from: `/perfil/${username}` } });
      return;
    }
    if (!profile || profile.isSelf) return;
    setReportOpen(true);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setStatus({ type: 'error', message: t('profile.copyFailed') });
    }
  };

  const applyAvatarUser = (nextUser) => {
    if (!nextUser) return;
    updateUser(nextUser);
    setProfile((current) => (current ? { ...current, avatarUrl: nextUser.avatarUrl || null } : current));
    setAvatarOpen(false);
  };

  const handleAvatarSave = async (file) => {
    setAvatarBusy(true);
    setStatus(null);
    try {
      const response = await userService.updateAvatar(file);
      applyAvatarUser(response?.data?.user);
      setStatus({ type: 'success', message: t('avatar.saved') });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || t('avatar.saveFailed') });
    } finally {
      setAvatarBusy(false);
    }
  };

  const handleAvatarRemove = async () => {
    setAvatarBusy(true);
    setStatus(null);
    try {
      const response = await userService.deleteAvatar();
      applyAvatarUser(response?.data?.user);
      setStatus({ type: 'success', message: t('avatar.removed') });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || t('avatar.removeFailed') });
    } finally {
      setAvatarBusy(false);
    }
  };

  const worksCount = hubCategories.reduce((sum, category) => sum + (category.items?.length || 0), 0);

  return (
    <main className="social-page profile-page">
      <div className="social-orb social-orb-a" aria-hidden="true" />
      <div className="social-orb social-orb-b" aria-hidden="true" />
      <SocialNav username={me?.username} />

      <div className="social-shell">
        {loading && (
          <div className="social-state">
            <FaSpinner className="social-spin" />
            <p>{t('profile.loading')}</p>
          </div>
        )}

        {!loading && error && (
          <div className="social-state">
            <h1>{t('profile.unavailable')}</h1>
            <p>{error}</p>
            <Link to="/explorar" className="social-btn">{t('profile.exploreUsers')}</Link>
          </div>
        )}

        {!loading && profile && (
          <>
            {status && (
              <div className={`social-alert ${status.type}`} role="status">{status.message}</div>
            )}

            <motion.section
              className="social-identity"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <div
                className={`profile-cover${profile.systemAccount?.enabled ? ' is-system' : ''}`}
                style={profile.systemAccount?.coverUrl
                  ? { backgroundImage: `url("${profile.systemAccount.coverUrl}")` }
                  : undefined}
                aria-hidden="true"
              >
                <span className="profile-cover-grid" />
                <span className="profile-cover-glow" />
              </div>
              <div className="social-identity-main">
                {profile.isSelf ? (
                  <AvatarEditTrigger
                    currentUrl={profile.avatarUrl}
                    displayName={displayName}
                    onClick={() => setAvatarOpen(true)}
                  />
                ) : (
                  <div className="social-avatar" aria-hidden="true">
                    {profile.avatarUrl ? (
                      <img src={profile.avatarUrl} alt="" />
                    ) : (
                      <span>{getInitials(displayName)}</span>
                    )}
                  </div>
                )}
                <div className="profile-identity-copy">
                  <p className="social-eyebrow"><span /> {t('profile.publicProfile')}</p>
                  <h1 className="profile-display-name">{displayName}</h1>
                  <p className="social-meta">
                    <span>@{profile.username}</span>
                    {profile.verified && (
                      <span className="social-verified" title={t('profile.verifiedAccount')}>
                        <FaCheckCircle aria-hidden="true" /> {t('profile.verified')}
                      </span>
                    )}
                  </p>
                  {(profile.badges || []).length > 0 && (
                    <div className="social-badges">
                      {profile.badges.map((badge) => {
                        const badgeKey = String(badge.slug || badge.name || '')
                          .toLowerCase()
                          .normalize('NFD')
                          .replace(/[\u0300-\u036f]/g, '')
                          .replace(/\s+/g, '-');
                        const badgeLabel = t(`badges.${badgeKey}`, { defaultValue: badge.name });
                        return (
                        <span
                          key={badge.id || badge._id || badge.slug}
                          className="social-badge"
                          style={{
                            color: badge.color,
                            backgroundColor: badge.backgroundColor,
                            borderColor: badge.color,
                          }}
                          title={badge.description || badgeLabel}
                        >
                          <FaAward aria-hidden="true" /> {badgeLabel}
                        </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="social-identity-actions">
                <div className="profile-social-stats">
                  <button type="button" className="social-stat-btn" onClick={() => openList('followers')}>
                    <span className="social-stat-icon"><FaUserFriends /></span>
                    <span className="social-stat-copy">
                      <strong>{profile.followersCount || 0}</strong>
                      <small>{t('profile.followers')}</small>
                    </span>
                    <FaChevronRight className="social-stat-arrow" />
                  </button>
                  <button type="button" className="social-stat-btn" onClick={() => openList('following')}>
                    <span className="social-stat-icon following"><FaFire /></span>
                    <span className="social-stat-copy">
                      <strong>{profile.followingCount || 0}</strong>
                      <small>{t('profile.following')}</small>
                    </span>
                    <FaChevronRight className="social-stat-arrow" />
                  </button>
                </div>
                <div className="profile-primary-actions">
                {!profile.isSelf && (
                  <button
                    type="button"
                    className="social-btn social-btn-ghost social-btn-icon"
                    aria-label={t('profile.reportUserAria')}
                    title={t('profile.report')}
                    onClick={handleReportUser}
                  >
                    <FaFlag />
                  </button>
                )}
                {!profile.isSelf && (
                  <button
                    type="button"
                    className={`social-btn ${profile.isFollowing ? 'social-btn-ghost' : ''}`}
                    onClick={handleFollowToggle}
                    disabled={followBusy}
                  >
                    {followBusy
                      ? <FaSpinner className="social-spin" />
                      : profile.isFollowing
                        ? <FaUserMinus />
                        : <FaUserPlus />}
                    {profile.isFollowing
                      ? t('profile.followingAction')
                      : profile.followsYou
                        ? t('profile.followBack')
                        : t('profile.follow')}
                  </button>
                )}
                {profile.isSelf && (
                  <Link to="/conta" className="social-btn social-btn-ghost">{t('profile.editProfile')}</Link>
                )}
                <button type="button" className="social-btn social-btn-ghost" onClick={copyLink}>
                  {copied ? <FaCheckCircle /> : <FaCopy />}
                  {copied ? t('profile.copied') : t('profile.copyLink')}
                </button>
                </div>
              </div>
            </motion.section>

            <div className="profile-grid">
              <aside className="social-card profile-about-card">
                <div className="social-card-head">
                  <div>
                    <span className="social-card-eyebrow">{t('profile.about')}</span>
                    <h2>{t('profile.meet', { name: displayName })}</h2>
                  </div>
                </div>
                <p className="profile-bio">
                  {profile.profile?.bio?.trim()
                    ? profile.profile.bio
                    : t('profile.noBio')}
                </p>

                <SkillsShowcase
                  skills={profile.profile?.skills}
                  isOwner={Boolean(profile.isSelf)}
                  displayName={displayName}
                  onStatus={setStatus}
                  onSkillsChange={(skills) => {
                    setProfile((current) => (
                      current
                        ? {
                          ...current,
                          profile: {
                            ...(current.profile || {}),
                            skills,
                          },
                        }
                        : current
                    ));
                  }}
                />

                <dl className="profile-meta-list">
                  <div>
                    <span className="profile-meta-icon"><FaCalendarAlt /></span>
                    <div>
                      <dt>{profile.systemAccount?.enabled ? t('profile.operatingSince') : t('profile.memberSince')}</dt>
                      <dd>{formatDate(profile.systemAccount?.operatingSince || profile.createdAt, language)}</dd>
                    </div>
                  </div>
                  <div>
                    <span className="profile-meta-icon"><FaCommentAlt /></span>
                    <div>
                      <dt>{t('profile.hub')}</dt>
                      <dd>
                        {t('profile.category', { count: hubCategories.length })}
                        {' · '}
                        {t('profile.works', { count: worksCount })}
                      </dd>
                    </div>
                  </div>
                </dl>
              </aside>

              <div className="profile-main-stack">
                <DevHubGallery
                  displayName={displayName}
                  isOwner={Boolean(profile.isSelf)}
                  categories={hubCategories}
                  onCategoriesChange={setHubCategories}
                  onStatus={setStatus}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {listOpen && (
        <div
          className="network-modal-backdrop"
          role="presentation"
          onClick={() => setListOpen(null)}
        >
          <div
            className="network-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="network-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="network-modal-head">
              <div className="network-modal-titles">
                <p className="network-modal-kicker">{t('profile.networkOf', { name: displayName })}</p>
                <h2 id="network-modal-title">
                  {listOpen === 'followers' ? t('profile.followers') : t('profile.following')}
                </h2>
                <p className="network-modal-lead">
                  {listOpen === 'followers'
                    ? t('profile.followersLead')
                    : t('profile.followingLead')}
                </p>
              </div>
              <button
                type="button"
                className="network-modal-close"
                onClick={() => setListOpen(null)}
                aria-label={t('profile.close')}
              >
                ×
              </button>
            </header>

            <div className="network-modal-body">
              {listLoading ? (
                <div className="network-modal-state">
                  <FaSpinner className="social-spin" />
                  <span>{t('common.loading')}</span>
                </div>
              ) : listUsers.length === 0 ? (
                <div className="network-modal-state">
                  <span className="network-modal-empty-icon" aria-hidden="true">
                    <FaUserFriends />
                  </span>
                  <strong>{t('profile.emptyList')}</strong>
                  <p>
                    {listOpen === 'followers'
                      ? t('profile.emptyFollowers')
                      : t('profile.emptyFollowing')}
                  </p>
                </div>
              ) : (
                <ul className="network-modal-list">
                  {listUsers.map((person) => {
                    const name = person.profile?.displayName || person.username;
                    return (
                      <li key={person.id}>
                        <Link
                          to={`/perfil/${person.username}`}
                          onClick={() => setListOpen(null)}
                          className="network-modal-row"
                        >
                          <span className="social-avatar md" aria-hidden="true">
                            {person.avatarUrl
                              ? <img src={person.avatarUrl} alt="" />
                              : getInitials(name)}
                          </span>
                          <span className="network-modal-row-copy">
                            <strong>{name}</strong>
                            <small>
                              @{person.username}
                              {person.verified ? ` · ${t('profile.verified')}` : ''}
                            </small>
                            <em>{person.profile?.bio || t('profile.communityMember')}</em>
                          </span>
                          <span className="network-modal-row-cta">
                            {t('profile.viewProfile')} <FaChevronRight aria-hidden="true" />
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <AvatarEditor
        open={avatarOpen}
        currentUrl={profile?.avatarUrl}
        displayName={displayName}
        busy={avatarBusy}
        onClose={() => {
          if (!avatarBusy) setAvatarOpen(false);
        }}
        onSave={handleAvatarSave}
        onRemove={handleAvatarRemove}
      />
      <ReportModal
        open={reportOpen && Boolean(profile?.id)}
        targetType="user"
        targetId={profile?.id}
        title={t('profile.reportProfileTitle', { username: profile?.username || 'user' })}
        description={t('profile.reportProfileDescription')}
        onClose={() => setReportOpen(false)}
        onSuccess={(message) => setStatus({ type: 'success', message })}
      />
    </main>
  );
}
